import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line } from 'recharts';

const COLORS = ['#4f46e5', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1'];

export default function MonthlyDashboard() {
  const { user } = useAuth();
  const [incomes, setIncomes] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [inc, exp] = await Promise.all([api.get('/incomes'), api.get('/expenses')]);
      setIncomes(Array.isArray(inc.data?.data) ? inc.data.data : Array.isArray(inc.data) ? inc.data : []);
      setExpenses(Array.isArray(exp.data?.data) ? exp.data.data : Array.isArray(exp.data) ? exp.data : []);
    } catch (e) { console.error('Failed to load data'); }
    finally { setLoading(false); }
  };

  const formatCurrency = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: user?.currency || 'USD' }).format(v || 0);

  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  // Build monthly data for ALL 12 months
  const monthlyData = months.map((monthName, idx) => {
    const monthNum = idx + 1;
    const monthIncomes = incomes.filter(i => {
      const d = new Date(i.date);
      return d.getMonth() + 1 === monthNum && d.getFullYear() === selectedYear;
    });
    const monthExpenses = expenses.filter(e => {
      const d = new Date(e.date);
      return d.getMonth() + 1 === monthNum && d.getFullYear() === selectedYear;
    });
    return {
      month: monthName,
      income: monthIncomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0),
      expenses: monthExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0),
      savings: monthIncomes.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0) - monthExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0)
    };
  });

  const totalIncome = monthlyData.reduce((s, m) => s + m.income, 0);
  const totalExpenses = monthlyData.reduce((s, m) => s + m.expenses, 0);
  const totalSavings = totalIncome - totalExpenses;

  // Category breakdown for the selected year
  const categoryData = expenses.reduce((acc, e) => {
    const cat = e.category || 'Other';
    const existing = acc.find(a => a.name === cat);
    if (existing) existing.value += parseFloat(e.amount) || 0;
    else acc.push({ name: cat, value: parseFloat(e.amount) || 0 });
    return acc;
  }, []);

  if (loading) return (
    <div className="flex justify-center items-center h-96">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
        <p className="mt-4 text-gray-500">Loading your annual report...</p>
      </div>
    </div>
  );

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Annual Financial Report</h1>
          <p className="text-gray-500">January - December {selectedYear}</p>
        </div>
        <select value={selectedYear} onChange={e => setSelectedYear(parseInt(e.target.value))}
          className="px-4 py-2.5 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 bg-white shadow-sm">
          {[2024, 2025, 2026, 2027].map(y => <option key={y} value={y}>{y}</option>)}
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Total Income', value: formatCurrency(totalIncome), color: 'green', icon: '📈', bg: 'bg-green-50' },
          { label: 'Total Expenses', value: formatCurrency(totalExpenses), color: 'red', icon: '📉', bg: 'bg-red-50' },
          { label: 'Net Savings', value: formatCurrency(totalSavings), color: 'indigo', icon: '💰', bg: 'bg-indigo-50' },
          { label: 'Transactions', value: incomes.length + expenses.length, color: 'purple', icon: '📊', bg: 'bg-purple-50' },
        ].map((s, i) => (
          <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-100 p-5 hover:shadow-md transition-all">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500 font-medium uppercase">{s.label}</span>
              <span className={`${s.bg} p-2 rounded-lg`}>{s.icon}</span>
            </div>
            <p className={`text-2xl font-bold text-${s.color}-600`}>{s.value}</p>
          </div>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Bar Chart - All 12 Months */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📊 Monthly Income vs Expenses</h3>
          <p className="text-xs text-gray-400 mb-4">Full year breakdown — January to December {selectedYear}</p>
          <ResponsiveContainer width="100%" height={350}>
            <BarChart data={monthlyData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '$' + (v >= 1000 ? (v/1000).toFixed(0) + 'k' : v)} />
              <Tooltip formatter={v => formatCurrency(v)} />
              <Legend />
              <Bar dataKey="income" fill="#10b981" radius={[6, 6, 0, 0]} name="Income" />
              <Bar dataKey="expenses" fill="#ef4444" radius={[6, 6, 0, 0]} name="Expenses" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* Line Chart - Savings Trend */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📈 Monthly Savings Trend</h3>
          <p className="text-xs text-gray-400 mb-4">Net savings per month — {selectedYear}</p>
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={monthlyData} margin={{ top: 5, right: 5, left: 0, bottom: 5 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} tickFormatter={v => '$' + (v >= 1000 ? (v/1000).toFixed(0) + 'k' : v)} />
              <Tooltip formatter={v => formatCurrency(v)} />
              <Legend />
              <Line type="monotone" dataKey="savings" stroke="#6366f1" strokeWidth={3} dot={{ r: 4, fill: '#6366f1' }} name="Net Savings" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Pie Chart + Monthly Table */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Expense Categories Pie */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">🍩 Expense Categories</h3>
          {categoryData.length > 0 ? (
            <>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie data={categoryData} cx="50%" cy="50%" innerRadius={60} outerRadius={110} paddingAngle={3} dataKey="value">
                    {categoryData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                  </Pie>
                  <Tooltip formatter={v => formatCurrency(v)} />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex flex-wrap gap-3 mt-4 justify-center">
                {categoryData.map((c, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-xs text-gray-600">
                    <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }}></span>
                    {c.name}: {formatCurrency(c.value)}
                  </span>
                ))}
              </div>
            </>
          ) : (
            <p className="text-gray-400 text-center py-16">No expenses recorded for {selectedYear}</p>
          )}
        </div>

        {/* Monthly Detail Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">📋 Monthly Breakdown</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100">
                  <th className="text-left py-3 px-2 text-xs font-medium text-gray-500 uppercase">Month</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-gray-500 uppercase">Income</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-gray-500 uppercase">Expenses</th>
                  <th className="text-right py-3 px-2 text-xs font-medium text-gray-500 uppercase">Savings</th>
                </tr>
              </thead>
              <tbody>
                {monthlyData.map((m, i) => (
                  <tr key={i} className="border-b border-gray-50 hover:bg-gray-50 transition-colors">
                    <td className="py-3 px-2 font-medium text-gray-900">{m.month}</td>
                    <td className="py-3 px-2 text-right text-green-600 font-medium">{formatCurrency(m.income)}</td>
                    <td className="py-3 px-2 text-right text-red-600 font-medium">{formatCurrency(m.expenses)}</td>
                    <td className={`py-3 px-2 text-right font-medium ${m.savings >= 0 ? 'text-indigo-600' : 'text-red-500'}`}>{formatCurrency(m.savings)}</td>
                  </tr>
                ))}
                <tr className="bg-gray-50 font-bold">
                  <td className="py-3 px-2 text-gray-900">TOTAL</td>
                  <td className="py-3 px-2 text-right text-green-700">{formatCurrency(totalIncome)}</td>
                  <td className="py-3 px-2 text-right text-red-700">{formatCurrency(totalExpenses)}</td>
                  <td className={`py-3 px-2 text-right ${totalSavings >= 0 ? 'text-indigo-700' : 'text-red-700'}`}>{formatCurrency(totalSavings)}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}